import React, { useState, useEffect } from 'react';

import { Flex } from '@chakra-ui/react';
import { Skeleton } from '@material-ui/lab';
import ClickToScrollNavBar from 'components/ClickToScrollNavBar';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AddFeedbackButton from 'screens/Levelup/LevelUpCF/components/addFeedbackButton/AddFeedbackButton';
import { useCFTableData } from 'screens/Levelup/LevelUpCF/hooks/dataHooks';
import FeedbackListRowView from 'screens/performanceReview/components/ContinuousFeedback/common/FeedbackListRowView';
import { selectMSTeamsContext } from 'selectors/msTeamsSelector';
import styled from 'styled-components';
import { MarginContainer, StyledBox, StyledTypography } from 'styles/common';

import { FEEDBACK_FORM_TYPE } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';
import theme from 'constants/theme';

import { getAllRequestedFeedbackList } from 'services/continuousFeedback';
import { showErrorSnackbar } from 'services/snackbar';

// Lists all feedbacks Requested/Shared
//
// We hide the footer in MSTeams View.
// Reason being, we are already providing Request/Share view in MSTeams Personal Tab.
// The actions on footer opens forms for Sharing/Requesting feedback. We want to avoid providing
// Multiple viewes for same feature as it may be quite confusing and inconsistent.

const FeedbackView = ({ handleClose, handleOpenShareForm, handleOpenRequestForm, defaultTab }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(null);
  // const [sharedFeedback, setSharedFeedback] = useState(null);
  const [requestedFeedback, setRequestedFeedback] = useState(null);

  const msTeamsContext = useSelector(selectMSTeamsContext);
  const isMsTeamsView = Boolean(msTeamsContext);

  const openFeedbackDetails = ({ formType, feedbackId }) => {
    const show = true;
    pushFeedbackParams({ history, formType, feedbackId, show });
  };

  const { data, isLoading, isIdle } = useCFTableData({
    feedbackStatus: 'SHARED',
    limit: 1000,
    offset: 0,
  });

  const getData = () => {
    setLoading(true);

    getAllRequestedFeedbackList()
      .then((res) => setRequestedFeedback(res))
      .catch((err) => dispatch(showErrorSnackbar('Something went wrong!')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getData();
  }, []);

  const FeedbackListNavItems = [
    {
      id: 'requested',
      header: () => <header>Requested</header>,
      component: () => (
        <MarginContainer topGutter={2}>
          <StyledTypography variant="body1" weight="bold">
            Requested Feedbacks
          </StyledTypography>
          {requestedFeedback === null || requestedFeedback === undefined ? null : (
            <>
              {requestedFeedback.map((item, index) => (
                <FeedbackListRowView
                  key={item.review.id}
                  employee={item.employee}
                  review={item.review}
                  handleClick={openFeedbackDetails}
                  type={FEEDBACK_FORM_TYPE.REQUESTED}
                />
              ))}
            </>
          )}
        </MarginContainer>
      ),
    },
    {
      id: 'shared',
      header: () => <header>Shared</header>,
      component: () => (
        <MarginContainer topGutter={2} bottomGutter={8}>
          <StyledTypography variant="body1" weight="bold">
            Shared Feedbacks
          </StyledTypography>
          {isLoading ? (
            <Container flexcontainer flexdirection="column" topGutter={2} bottomGutter={2} padding="0 2rem">
              {Array(5)
                .fill(null)
                .map((item, index) => (
                  <Skeleton />
                ))}
            </Container>
          ) : (
            <>
              {data?.values.map((item, index) => (
                <FeedbackListRowView
                  key={index}
                  employee={item?.employee?.value?.employeeMiniProfiles[0]}
                  review={{
                    visibility: item?.comment?.value?.visibility,
                    submissionDateFormatted: {
                      timeStamp: item?.date?.value,
                      color: 'blue',
                      reason: null,
                      isCrossed: false,
                    },
                    feedbackType: FEEDBACK_FORM_TYPE.PROVIDED,
                    id: item?.comment?.uuid,
                  }}
                  handleClick={openFeedbackDetails}
                  type={FEEDBACK_FORM_TYPE.PROVIDED}
                />
              ))}
            </>
          )}
        </MarginContainer>
      ),
    },
  ];

  return (
    <Container flexcontainer flexdirection="column">
      <Header>
        <StyledTypography weight={600} color={theme.palette.text.heading}>
          &nbsp; Feedback List
        </StyledTypography>
      </Header>
      {loading === false ? (
        <StyledClickToScrollNavBar
          sectionList={FeedbackListNavItems}
          loading={loading}
          showDividers
          defaultSelectedIndex={defaultTab || 0}
        />
      ) : (
        <Container flexcontainer flexdirection="column" topGutter={2} bottomGutter={2} padding="0 2rem">
          {Array(5)
            .fill(null)
            .map((item, index) => (
              <StyledSkeleton key={index} component="div" width="100%" height="5%" animation="wave" variant="rect" />
            ))}
        </Container>
      )}

      <Flex sx={{ padding: '0.7rem 2rem', borderTop: '1px solid #eaeaea', minHeight: '3rem', height: '8vh' }}>
        <AddFeedbackButton
          onClickShareFeedback={(isSharePublicPraise) => handleOpenShareForm(isSharePublicPraise)}
          onClickRequestFeedback={handleOpenRequestForm}
        />
      </Flex>
    </Container>
  );
};

export default FeedbackView;

const Container = styled(StyledBox)`
  position: relative;
  height: 100%;
`;
const StyledClickToScrollNavBar = styled(ClickToScrollNavBar)`
  margin: 1rem 2rem;
`;

const StyledSkeleton = styled(Skeleton)`
  margin-top: 1rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 1.5rem 18px;
  // border-bottom: 1px solid #eaeaea;
  min-height: 3rem;
  height: 8vh;
`;
